import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
  IconButton,
} from '@mui/material';
import { CloudUpload, Delete } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { TrainingContentDto } from '../../../api/types/dtos/training';
import { 
  useUpdateTrainingContent,
  useUpdateWithFileTrainingContent,
} from '../../../api/hooks/useTrainingContents';
import { useDataTypes } from '../../../api/hooks/useDataTypes';
import { useOrganization } from '../../../contexts/OrganizationContext';

interface EditContentModalProps {
  open: boolean;
  onClose: () => void;
  content: TrainingContentDto | null;
  trainingId: string;
  moduleId: string;
  topicId: string;
}

const EditContentModal: React.FC<EditContentModalProps> = ({
  open,
  onClose,
  content,
  trainingId,
  moduleId,
  topicId,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [sequence, setSequence] = useState(1);
  const [durationMinutes, setDurationMinutes] = useState<number | ''>('');
  const [progressContribution, setProgressContribution] = useState(100);
  const [file, setFile] = useState<File | null>(null);
  const [useFileUpload, setUseFileUpload] = useState(false);
  
  const { selectedOrganization } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const { data: dataTypes } = useDataTypes();

  const updateMutation = useUpdateTrainingContent(
    selectedOrganization?.id || '',
    trainingId,
    moduleId,
    topicId
  );
  const updateWithFileMutation = useUpdateWithFileTrainingContent(
    selectedOrganization?.id || '',
    trainingId,
    moduleId,
    topicId
  );

  const isPending = updateMutation.isPending || updateWithFileMutation.isPending;

  useEffect(() => {
    if (content) {
      setName(content.name);
      setDescription(content.description || '');
      setUrl(content.url);
      setSequence(content.sequence);
      setDurationMinutes(content.durationMinutes || '');
      setProgressContribution(content.progressContribution);
      setUseFileUpload(false);
      setFile(null);
    }
  }, [content]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
    event.target.value = '';
  };

  const getTypeNameFromMime = (mimeType: string): string => {
    if (mimeType.startsWith('video/')) return 'video_url';
    if (mimeType.startsWith('audio/')) return 'audio_url';
    if (mimeType.startsWith('image/')) return 'image_url';
    if (mimeType === 'application/pdf') return 'pdf_url';
    if (mimeType.includes('document') || mimeType.includes('word')) return 'document_url';
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'spreadsheet_url';
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'presentation_url';
    if (mimeType.startsWith('text/')) return 'text_url';
    return 'file_url';
  };

  const getDataTypeIdFromMime = (mimeType: string): string | null => {
    if (!dataTypes || dataTypes.length === 0) return null;
    const typeName = getTypeNameFromMime(mimeType);
    const matchingType = dataTypes.find((dt) => dt.name === typeName);
    return matchingType?.id ?? null;
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      enqueueSnackbar('El nombre es requerido', { variant: 'error' });
      return;
    }

    if (!content?.id) {
      enqueueSnackbar('No se puede actualizar el contenido', { variant: 'error' });
      return;
    }

    if (!useFileUpload && !url.trim()) {
      enqueueSnackbar('La URL es requerida si no se sube un archivo', {
        variant: 'error',
      });
      return;
    }

    if (useFileUpload && !file) {
      enqueueSnackbar('Debe seleccionar un archivo', { variant: 'error' });
      return;
    }

    if (progressContribution < 0 || progressContribution > 100) {
      enqueueSnackbar('La contribución al progreso debe estar entre 0 y 100', {
        variant: 'error',
      });
      return;
    }

    try {
      if (useFileUpload && file) {
        const typeId = getDataTypeIdFromMime(file.type);
        if (!typeId) {
          enqueueSnackbar(
            `No se encontró un DataType con nombre "${getTypeNameFromMime(file.type)}" en el sistema.`,
            { variant: 'error' }
          );
          return;
        }

        await updateWithFileMutation.mutateAsync({
          id: content.id,
          data: {
            file,
            name: name.trim(),
            description: description.trim() || undefined,
            sequence,
            typeId,
            durationMinutes: durationMinutes || undefined,
            progressContribution,
          },
        });
      } else {
        await updateMutation.mutateAsync({
          id: content.id,
          data: {
            name: name.trim(),
            description: description.trim() || undefined,
            url: url.trim(),
            sequence,
            durationMinutes: durationMinutes || undefined,
            progressContribution,
            topicId: content.topicId,
            typeId: content.typeId,
            isActive: content.isActive,
          },
        });
      }

      enqueueSnackbar('Contenido actualizado exitosamente', { variant: 'success' });
      onClose();
    } catch (error) {
      enqueueSnackbar('Error al actualizar el contenido', { variant: 'error' });
      console.error('Error updating content:', error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Editar Contenido</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Nombre"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="Descripción"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={useFileUpload}
                onChange={(e) => {
                  setUseFileUpload(e.target.checked);
                  if (!e.target.checked) {
                    setFile(null);
                  }
                }}
              />
            }
            label="Subir nuevo archivo"
          />

          {!useFileUpload ? (
            <TextField
              label="URL del Contenido"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
              fullWidth
              placeholder="https://..."
            />
          ) : (
            <Box>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                fullWidth
                disabled={isPending}
              >
                {file ? file.name : 'Seleccionar Archivo'}
                <input
                  type="file"
                  hidden
                  onChange={handleFileChange}
                  accept="*/*"
                  disabled={isPending}
                />
              </Button>
              {file && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="caption">
                    {file.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setFile(null)}
                    disabled={isPending}
                    color="error"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          )}

          <TextField
            label="Duración (minutos)"
            type="number"
            value={durationMinutes}
            onChange={(e) =>
              setDurationMinutes(e.target.value ? parseInt(e.target.value) : '')
            }
            fullWidth
            InputProps={{ 
              inputProps: { 
                min: 0,
                onWheel: (e) => e.currentTarget.blur()
              } 
            }}
          />

          <TextField
            label="Secuencia"
            type="number"
            value={sequence}
            onChange={(e) => setSequence(parseInt(e.target.value) || 1)}
            required
            fullWidth
            InputProps={{ 
              inputProps: { 
                min: 1,
                onWheel: (e) => e.currentTarget.blur()
              } 
            }}
          />

          <TextField
            label="Contribución al Progreso (%)"
            type="number"
            value={progressContribution}
            onChange={(e) =>
              setProgressContribution(parseInt(e.target.value) || 0)
            }
            required
            fullWidth
            helperText="La suma de todos los contenidos del tema debe ser 100%"
            InputProps={{ 
              inputProps: { 
                min: 0, 
                max: 100,
                onWheel: (e) => e.currentTarget.blur()
              } 
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose} 
          disabled={isPending}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isPending}
          startIcon={isPending && <CircularProgress size={20} />}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditContentModal;
